import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Heart, Sparkles, Loader2, Check, AlertTriangle, Download, ShoppingBag, ExternalLink, Share2, Crown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edge-error";
import { useSignedImage } from "@/hooks/useSignedImage";
import { BeforeAfter } from "@/components/BeforeAfter";
import { FitScoreRing } from "@/components/FitScoreRing";
import { uploadToBucket, signedUrl, dataUrlToBlob } from "@/lib/storage";
import { errorMessage } from "@/lib/error-message";
import { cn } from "@/lib/utils";
import { OUTFIT_TEMPLATES, type CustomizeData } from "@/components/CustomizeOutfitDialog";
import { CustomizeOutfitInline } from "@/pages/style-outfit/CustomizeOutfitInline";
import { GenerationOverlay } from "@/components/GenerationOverlay";

import { detectBrandFromUrl } from "@/lib/brands";
import { usePaywallTriggers, type PaywallVariant } from "@/hooks/usePaywallTriggers";
import { useFreeTierGate } from "@/contexts/FreeTierGateContext";
import { PaywallModal } from "@/components/PaywallModal";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { AuraScoreCard, type AuraData } from "@/components/AuraScoreCard";
import { Users } from "lucide-react";
import { SEO } from "@/components/SEO";
import { showInterstitial, shouldShowInterstitial } from "@/lib/native-ads";
import { isAndroidApp, isNativeApp } from "@/lib/platform";
import { saveImageToDevice } from "@/lib/save-image";
import { shareImageNative } from "@/lib/native-share";
import { runSaveShareAdGate } from "@/lib/save-share-ad-gate";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { ExpiryBanner } from "@/components/ExpiryBanner";
import { SubmitFeaturedSheet } from "@/components/history/SubmitFeaturedSheet";

interface TryOn {
  id: string;
  user_id: string;
  user_photo_url: string;
  product_url: string | null;
  product_image_url: string | null;
  generated_image_url: string | null;
  outfit_image_url: string | null;
  image_expires_at: string | null;
  outfit_items: { occasion?: string; items?: { category: string; description: string; color: string }[] } | null;
  product_category: string | null;
  product_color: string | null;
  product_style: string | null;
  suits: boolean | null;
  fit_score: number | null;
  style_match: number | null;
  color_harmony: number | null;
  accessory_balance: number | null;
  trend_alignment: number | null;
  occasion_fit: number | null;
  reason: string | null;
  improvements: string[] | null;
  is_favorite: boolean;
  aura_score: number | null;
  aura_silhouette: number | null;
  aura_proportions: number | null;
  aura_color_season: number | null;
  aura_statement: number | null;
  aura_photo_readiness: number | null;
  aura_verdict: string | null;
  aura_tips: string[] | null;
}

// Derive the suitability label from the numeric fit_score so label and number always agree.
const suitabilityFromScore = (score: number | null): {
  label: string;
  tone: "success" | "primary" | "warning";
} => {
  if (typeof score !== "number") return { label: "Pending review", tone: "primary" };
  if (score >= 7.5) return { label: "Suits you", tone: "success" };
  if (score >= 5)   return { label: "Decent fit", tone: "primary" };
  return { label: "Could be better", tone: "warning" };
};

const Result = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { runGate } = useFreeTierGate();
  const isMobile = useIsMobile();
  const [tryon, setTryon] = useState<TryOn | null>(null);
  const [generatingOutfit, setGeneratingOutfit] = useState(false);
  const [scoringFit, setScoringFit] = useState(false);
  const { checkAfterTryon, checkAfterAction } = usePaywallTriggers();
  const [paywall, setPaywall] = useState<PaywallVariant | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyDefaultPack, setBuyDefaultPack] = useState<string | undefined>(undefined);
  const [submitFeaturedOpen, setSubmitFeaturedOpen] = useState(false);
  const tryonCheckedRef = useRef(false);

  // SEO handled by <SEO /> component in render.

  const load = async () => {
    if (!id) return;
    const { data, error } = await supabase.from("tryons").select("*").eq("id", id).maybeSingle();
    if (error) {
      toast({ title: "Couldn't load result", description: error.message, variant: "destructive" });
      return;
    }
    setTryon(data as unknown as TryOn);
  };
  useEffect(() => { load(); }, [id]);

  // Fire paywall check after a successful tryon loads.
  useEffect(() => {
    if (!tryon || tryonCheckedRef.current) return;
    tryonCheckedRef.current = true;
    const trig = checkAfterTryon({ id: tryon.id, fit_score: tryon.fit_score });
    if (trig) setPaywall(trig.variant);

    // Free-tier interstitial: show after the result reveals, capped to every
    // 2nd generation. Native Android only; paid users skip via server check.
    if (isAndroidApp() && tryon.generated_image_url) {
      (async () => {
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("credits_purchased_total")
            .eq("user_id", tryon.user_id)
            .maybeSingle();
          const purchased = Number(profile?.credits_purchased_total ?? 0);
          if (purchased > 0) return;
          if (!shouldShowInterstitial()) return;
          // Small delay so the user sees the reveal animation first.
          setTimeout(() => { void showInterstitial(); }, 1800);
        } catch {
          /* non-fatal */
        }
      })();
    }
  }, [tryon, checkAfterTryon]);

  const beforeUrl = useSignedImage("user-photos", tryon?.user_photo_url);
  const afterUrl = useSignedImage("tryon-results", tryon?.generated_image_url);
  const outfitUrl = useSignedImage("tryon-results", tryon?.outfit_image_url);

  // Auto-save the generated image to the user's phone (when enabled).
  // Default ON for native installs since the cloud copy auto-deletes in 24h.
  useEffect(() => {
    if (!tryon || !afterUrl) return;
    (async () => {
      const { isAutoSaveEnabled, wasAutoSaved, markAutoSaved } = await import("@/lib/local-results");
      if (!isAutoSaveEnabled() || wasAutoSaved(tryon.id)) return;
      try {
        await saveImageToDevice(afterUrl, `fitaura-${tryon.id}.png`);
        markAutoSaved(tryon.id);
      } catch (e) {
        // Best-effort. The banner still nudges the user to save manually.
        console.warn("Auto-save failed", e);
      }
    })();
  }, [tryon?.id, afterUrl]);

  const toggleFavorite = async () => {
    if (!tryon) return;
    const next = !tryon.is_favorite;
    setTryon({ ...tryon, is_favorite: next });
    await supabase.from("tryons").update({ is_favorite: next }).eq("id", tryon.id);
    if (next) {
      const trig = checkAfterAction("favorite");
      if (trig) setPaywall(trig.variant);
    }
  };




  // Save image to device — Android/iOS writes to public Documents/FitAura
  // (visible in Files & gallery apps), web triggers a browser download.
  const downloadImage = async (url: string | null, filename: string) => {
    if (!url) return;
    const ok = await runSaveShareAdGate(user?.id);
    if (!ok) return;
    try {
      const result = await saveImageToDevice(url, filename);
      toast({
        title: result.location === "gallery" ? "Saved to your device" : "Image downloaded",
        description: result.location === "gallery"
          ? "Find it in Files → Documents → FitAura."
          : undefined,
      });
    } catch (e) {
      toast({
        title: "Save failed",
        description: errorMessage(e),
        variant: "destructive",
      });
    }
  };

  // Direct native share — sends the actual image file to the OS share sheet
  // (WhatsApp, Instagram, Messages, etc.) without showing an intermediate menu.
  // Falls back to the polished ShareMenu only if Web Share API is unavailable.
  const shareImageDirect = async (url: string | null) => {
    if (!url) return;
    const ok = await runSaveShareAdGate(user?.id);
    if (!ok) return;
    const filename = `fitaura-${tryon?.id ?? "look"}.png`;
    try {
      const result = await shareImageNative({
        url,
        filename,
        title: "My FitAura AI look ✨",
        text: "Check out my FitAura AI look ✨",
        dialogTitle: "Share your look",
      });
      if (result.shared) {
        const trig = checkAfterAction("share");
        if (trig) setPaywall(trig.variant);
        return;
      }
      if (result.cancelled) return;
      // No share path available at all — save so the user can post manually.
      await saveImageToDevice(url, filename);
      toast({ title: "Image saved", description: "Share it from your gallery." });
    } catch (e) {
      if ((e as Error)?.name === "AbortError") return;
      toast({ title: "Share failed", description: errorMessage(e), variant: "destructive" });
    }
  };

  // On-demand fit scoring. Free for the user (Gemini cost is ~$0.0005/call,
  // negligible). Image generation already cost 1 credit. We only show the CTA
  // when fit_score is null, and the edge function is idempotent so accidental
  // double-taps don't double-call Gemini.
  const scoreFit = async () => {
    if (!tryon || scoringFit) return;
    setScoringFit(true);
    try {
      const { data, error } = await supabase.functions.invoke("score-fit", {
        body: { tryonId: tryon.id },
      });
      if (error) {
        const ctx = (error as { context?: { status?: number } }).context;
        if (ctx?.status === 429) {
          toast({ title: "Rate limited", description: "Try again in a moment.", variant: "destructive" });
          return;
        }
        throw error;
      }
      if (data?.fit) {
        // Merge into local state so the score breakdown renders without a refetch.
        setTryon((prev) =>
          prev ? { ...prev, ...data.fit } as TryOn : prev,
        );
        toast({ title: "Style scored ✨" });
      }
    } catch (e) {
      const { message } = await readEdgeError(e);
      toast({
        title: "Couldn't score this look",
        description: message,
        variant: "destructive",
      });
    } finally {
      setScoringFit(false);
    }
  };

  const generateOutfit = async (custom?: CustomizeData) => {
    if (!user || !tryon) return;
    // Free-tier ad-gate (Android only). Re-rolls also count toward the lifetime 3.
    const allowed = await runGate();
    if (!allowed) return;
    setGeneratingOutfit(true);
    try {
      // Use the try-on after-image as the canvas so the styled outfit visually
      // matches what the user sees on the Result page. Fall back to the original
      // upload if the after-image is missing (e.g. older or failed try-ons).
      const userUrl = tryon.generated_image_url
        ? await signedUrl("tryon-results", tryon.generated_image_url)
        : await signedUrl("user-photos", tryon.user_photo_url);
      const baseUrl = tryon.product_image_url
        ? await signedUrl("product-images", tryon.product_image_url)
        : null;
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      // Upload reference photos (if any) and collect signed URLs
      const referenceImageUrls: string[] = [];
      if (custom?.referenceFiles?.length) {
        for (const f of custom.referenceFiles) {
          const path = await uploadToBucket("product-images", user.id, f, "jpg");
          referenceImageUrls.push(await signedUrl("product-images", path));
        }
      }
      // Try to extract product images from reference links (skip empty/invalid)
      const validLinks = (custom?.referenceLinks ?? [])
        .map((l) => l?.trim())
        .filter((l): l is string => !!l && /^https?:\/\//i.test(l));
      for (const link of validLinks) {
        try {
          const { data: ext } = await supabase.functions.invoke("extract-product", {
            body: { url: link },
          });
          if (ext?.imageUrl) referenceImageUrls.push(ext.imageUrl);
        } catch {
          /* ignore individual link failures */
        }
      }

      const { data, error } = await supabase.functions.invoke("generate-outfit", {
        body: {
          userPhotoUrl: userUrl,
          baseProductImageUrl: baseUrl,
          baseProduct: baseUrl
            ? {
                category: tryon.product_category,
                color: tryon.product_color,
                style: tryon.product_style,
              }
            : null,
          profile,
          customPrompt: custom?.prompt || undefined,
          referenceImageUrls: referenceImageUrls.length ? referenceImageUrls : undefined,
        },
      });

      if (error) {
        const ctx = (error as { context?: { status?: number } }).context;
        if (ctx?.status === 429) {
          toast({ title: "Rate limited", description: "Try again in a moment.", variant: "destructive" });
          return;
        }
        if (ctx?.status === 402) {
          toast({ title: "Out of credits", description: "Top up to keep generating.", variant: "destructive" });
          return;
        }
        throw error;
      }

      const blob = dataUrlToBlob(data.outfitImageDataUrl);
      const path = await uploadToBucket("tryon-results", user.id, blob, "png");

      await supabase
        .from("tryons")
        .update({
          outfit_image_url: path,
          outfit_items: data.outfit,
          // 24h TTL — the hourly sweeper deletes the file after this time.
          image_expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        })
        .eq("id", tryon.id);

      await load();
      toast({ title: "Full outfit ready ✨" });
    } catch (e) {
      const { message } = await readEdgeError(e);
      toast({
        title: "Outfit generation failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setGeneratingOutfit(false);
    }
  };

  if (!tryon) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const body = (
    <div className="mx-auto w-full max-w-md px-5 pt-3 animate-fade-in lg:max-w-7xl lg:px-8 lg:pt-6">
      <span className="font-display hidden text-[10px] font-semibold uppercase tracking-[0.3em] text-primary lg:inline">— RESULT</span>
      <div className="mb-3 flex items-center justify-between lg:col-span-12 lg:mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            onClick={() => setSubmitFeaturedOpen(true)}
            disabled={!afterUrl}
            className="h-9 rounded-full bg-gradient-to-r from-primary to-accent px-3 text-xs font-bold text-primary-foreground shadow-soft"
          >
            <Crown className="mr-1 h-3.5 w-3.5" /> Get Featured
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleFavorite} aria-label="Favorite">
            <Heart className={cn("h-5 w-5", tryon.is_favorite && "fill-primary text-primary")} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => downloadImage(afterUrl, `tryfit-${tryon.id}.png`)}
            disabled={!afterUrl}
            aria-label="Download"
          >
            <Download className="h-5 w-5" />
          </Button>
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => shareImageDirect(afterUrl)}
              disabled={!afterUrl}
              aria-label="Share"
            >
              <Share2 className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
      {/* 24h auto-delete warning — sticky reminder to download before the cloud copy is wiped. */}
      <ExpiryBanner
        expiresAt={tryon.image_expires_at}
        hasImage={!!tryon.generated_image_url}
        onDownload={() => downloadImage(afterUrl, `fitaura-${tryon.id}.png`)}
        downloadDisabled={!afterUrl}
        className="mx-auto mb-3 w-full max-w-[420px]"
      />
      {beforeUrl && afterUrl ? (
        <div className="mx-auto w-full max-w-[420px]">
          <BeforeAfter beforeSrc={beforeUrl} afterSrc={afterUrl} />
        </div>
      ) : (
        <div className="mx-auto aspect-[4/5] w-full max-w-[420px] animate-pulse rounded-2xl bg-muted" />
      )}



      <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        AI-generated — may contain inaccuracies.
      </p>

      {/* Suitability badge — derived from fit_score so the label always agrees with the number. */}
      {(() => {
        const s = suitabilityFromScore(tryon.fit_score);
        const toneClasses =
          s.tone === "success" ? "bg-success/15 text-success" :
          s.tone === "warning" ? "bg-warning/15 text-warning" :
          "bg-primary/15 text-primary";
        const Icon = s.tone === "warning" ? AlertTriangle : Check;
        return (
          <div className="mt-5 flex items-center justify-between rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="flex items-center gap-3">
              <div className={cn("flex h-10 w-10 items-center justify-center rounded-full", toneClasses)}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold">{s.label}</p>
                <p className="text-xs text-muted-foreground">
                  {tryon.product_color} {tryon.product_category}
                </p>
              </div>
            </div>
            {typeof tryon.fit_score === "number" && <FitScoreRing score={tryon.fit_score} size={72} />}
          </div>
        );
      })()}

      {/* Buy on [Brand] CTA — high-intent click target after a try-on.
          Affiliate tracking is auto-applied by the Cuelinks snippet in index.html. */}
      {tryon.product_url && (() => {
        const brand = detectBrandFromUrl(tryon.product_url);
        const label = brand ? `Buy on ${brand.name}` : "Buy this product";
        return (
          <a
            href={tryon.product_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className={cn(
              "mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-white shadow-elevated transition-all hover:opacity-95 active:scale-[0.99]",
              brand ? `bg-gradient-to-br ${brand.gradient}` : "gradient-primary",
            )}
          >
            <ShoppingBag className="h-5 w-5" />
            {label}
            <ExternalLink className="h-4 w-4 opacity-80" />
          </a>
        );
      })()}

      {/* Get Fit Score CTA — appears only when scoring hasn't run yet.
          Free for the user; runs an on-demand Gemini vision call. */}
      {tryon.generated_image_url && typeof tryon.fit_score !== "number" && (
        <div className="mt-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-4 shadow-soft animate-fade-in">
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Get your Style Score</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                AI grades color harmony, accessory balance, trend &amp; occasion — free.
              </p>
            </div>
          </div>
          <Button
            className="h-11 w-full gradient-primary text-primary-foreground"
            onClick={scoreFit}
            disabled={scoringFit}
          >
            {scoringFit ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scoring your look…</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Score this look (Free)</>
            )}
          </Button>
        </div>
      )}

      {/* Style breakdown — AI-graded from the generated image */}
      {(typeof tryon.style_match === "number" ||
        typeof tryon.color_harmony === "number" ||
        typeof tryon.accessory_balance === "number" ||
        typeof tryon.trend_alignment === "number" ||
        typeof tryon.occasion_fit === "number") && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold">Style breakdown</span>
            {typeof tryon.style_match === "number" && (
              <span className="text-lg font-bold tabular-nums text-primary">
                {tryon.style_match}
                <span className="text-xs font-normal text-muted-foreground">/100</span>
              </span>
            )}
          </div>
          <div className="space-y-2.5">
            <ScoreBar label="Color harmony" value={tryon.color_harmony} />
            <ScoreBar label="Accessory balance" value={tryon.accessory_balance} />
            <ScoreBar label="Trend alignment" value={tryon.trend_alignment} />
            <ScoreBar label="Occasion fit" value={tryon.occasion_fit} />
          </div>
        </div>
      )}

      {/* Aura Score — deeper, on-demand audit (0.10 credits) */}
      {tryon.generated_image_url && (
        <AuraScoreCard
          tryonId={tryon.id}
          data={{
            aura_score: tryon.aura_score,
            aura_silhouette: tryon.aura_silhouette,
            aura_proportions: tryon.aura_proportions,
            aura_color_season: tryon.aura_color_season,
            aura_statement: tryon.aura_statement,
            aura_photo_readiness: tryon.aura_photo_readiness,
            aura_verdict: tryon.aura_verdict,
            aura_tips: tryon.aura_tips,
          }}
        />
      )}

      {tryon.reason && (
        <Card className="mt-4 p-4 shadow-soft">
          <p className="text-sm leading-relaxed">{tryon.reason}</p>
        </Card>
      )}

      {Array.isArray(tryon.improvements) && tryon.improvements.length > 0 && (
        <Card className="mt-4 p-4 shadow-soft">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">
            Suggestions
          </p>
          <ul className="space-y-2">
            {tryon.improvements.map((s, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                {s}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Full outfit — separate section */}
        </div>
        <section className="mt-8 border-t border-border pt-6 lg:col-span-5 lg:mt-0 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="text-base font-bold">Style a full outfit</h2>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Build a complete look around this item. Your face stays exactly the same.
        </p>

        {!tryon.outfit_image_url ? (
          <CustomizeOutfitInline busy={generatingOutfit} onGenerate={generateOutfit} />
        ) : (
          <>
            <Card className="overflow-hidden shadow-soft">
              <div className="flex items-start justify-between gap-2 border-b border-border bg-accent/40 p-3">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    <Sparkles className="h-4 w-4 text-primary" /> Full outfit
                  </p>
                  {tryon.outfit_items?.occasion && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {tryon.outfit_items.occasion}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => downloadImage(outfitUrl, `tryfit-outfit-${tryon.id}.png`)}
                  disabled={!outfitUrl}
                  aria-label="Download outfit"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
              {outfitUrl && (
                <img src={outfitUrl} alt="Full outfit" className="aspect-[3/4] w-full object-cover" />
              )}
              {tryon.outfit_items?.items && (
                <div className="space-y-2 p-4">
                  {tryon.outfit_items.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="font-medium capitalize">{it.category}</span>
                      <Badge variant="secondary" className="font-normal">
                        {it.color}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <div className="mt-4">
              <CustomizeOutfitInline busy={generatingOutfit} onGenerate={generateOutfit} />
            </div>
          </>
        )}
      </section>
      </div>

      <Button
        className="my-6 h-12 w-full gradient-primary text-primary-foreground"
        onClick={() => navigate("/app")}
      >
        Try another item
      </Button>

      <GenerationOverlay
        open={generatingOutfit}
        stage="Generating your outfit…"
      />

      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} defaultPackId={buyDefaultPack} />

      <SubmitFeaturedSheet
        tryonId={submitFeaturedOpen ? tryon.id : null}
        onClose={() => setSubmitFeaturedOpen(false)}
      />

      <PaywallModal
        open={!!paywall}
        variant={paywall}
        onClose={() => setPaywall(null)}
        onDismiss={() => setPaywall(null)}
        onBuy={(packId) => {
          setBuyDefaultPack(packId);
          setBuyOpen(true);
          setPaywall(null);
        }}
      />
    </div>
  );

  return (
    <>
      <SEO title="Your Try-On Result – FitAura AI" description="View, share, and refine your AI virtual try-on result." noindex />
      {isMobile ? <MobileShell title="Result">{body}</MobileShell> : body}
    </>
  );
};

const ScoreBar = ({ label, value }: { label: string; value: number | null }) => {
  if (typeof value !== "number") return null;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums">{value}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full gradient-primary transition-all"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  );
};

export default Result;
